import { createTheme } from '@mui/material';

// ─── Light Professional Palette ───────────────────────────────────────────────
const palette = {
  mode: 'light',
  primary: {
    main: '#0D9488',      // Teal
    light: '#5EEAD4',
    dark: '#0F766E',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#4F46E5',      // Indigo
    light: '#818CF8',
    dark: '#3730A3',
    contrastText: '#ffffff',
  },
  background: {
    default: '#F8FAFC',   // Light slate gray
    paper: '#FFFFFF',
  },
  text: {
    primary: '#111827',
    secondary: '#4B5563',
    disabled: '#9CA3AF',
  },
  action: {
    active: '#0D9488',
    hover: 'rgba(13, 148, 136, 0.06)',
    selected: 'rgba(13, 148, 136, 0.10)',
  },
  divider: '#E5E7EB',
  success: {
    main: '#059669',
    light: '#D1FAE5',
    dark: '#047857',
  },
  warning: {
    main: '#D97706',
    light: '#FEF3C7',
    dark: '#B45309',
  },
  error: {
    main: '#DC2626',
    light: '#FEE2E2',
    dark: '#B91C1C',
  },
  info: {
    main: '#2563EB',
    light: '#DBEAFE',
    dark: '#1D4ED8',
  },
};

// ─── Font Families ────────────────────────────────────────────────────────────
const fontDisplay = '"Instrument Serif", Georgia, "Times New Roman", serif';
const fontSans = '"DM Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
const fontMono = '"JetBrains Mono", "Fira Code", "Consolas", monospace';

export const theme = createTheme({
  palette,
  shape: {
    borderRadius: 8,
  },
  shadows: [
    'none',
    '0 1px 2px rgba(0,0,0,0.05)',                                          // 1
    '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)',             // 2
    '0 4px 6px -1px rgba(0,0,0,0.07), 0 2px 4px -2px rgba(0,0,0,0.05)',   // 3
    '0 10px 15px -3px rgba(0,0,0,0.08), 0 4px 6px -4px rgba(0,0,0,0.04)', // 4
    '0 20px 25px -5px rgba(0,0,0,0.08), 0 8px 10px -6px rgba(0,0,0,0.04)',// 5
    ...Array(19).fill('0 20px 25px -5px rgba(0,0,0,0.08), 0 8px 10px -6px rgba(0,0,0,0.04)'), // 6-24
  ],
  typography: {
    fontFamily: fontSans,
    // All headings use sans-serif for clean SaaS look
    h1: {
      fontFamily: fontSans,
      fontWeight: 800,
      fontSize: '2.25rem',
      letterSpacing: '-0.02em',
      lineHeight: 1.15,
    },
    h2: {
      fontFamily: fontSans,
      fontWeight: 700,
      fontSize: '1.875rem',
      letterSpacing: '-0.01em',
      lineHeight: 1.2,
    },
    h3: {
      fontFamily: fontSans,
      fontWeight: 700,
      fontSize: '1.25rem',
      letterSpacing: '-0.01em',
      lineHeight: 1.35,
    },
    h4: {
      fontFamily: fontSans,
      fontWeight: 600,
      fontSize: '1.125rem',
      lineHeight: 1.4,
    },
    h5: {
      fontFamily: fontSans,
      fontWeight: 600,
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    h6: {
      fontFamily: fontSans,
      fontWeight: 600,
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    subtitle1: {
      fontFamily: fontSans,
      fontWeight: 500,
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    subtitle2: {
      fontFamily: fontSans,
      fontWeight: 500,
      fontSize: '0.875rem',
      lineHeight: 1.6,
    },
    body1: {
      fontFamily: fontSans,
      fontWeight: 400,
      fontSize: '1rem',
      lineHeight: 1.7,
    },
    body2: {
      fontFamily: fontSans,
      fontWeight: 400,
      fontSize: '0.875rem',
      lineHeight: 1.6,
    },
    button: {
      fontFamily: fontSans,
      fontWeight: 600,
      fontSize: '0.875rem',
      letterSpacing: '0.01em',
    },
    caption: {
      fontFamily: fontSans,
      fontWeight: 400,
      fontSize: '0.75rem',
      lineHeight: 1.5,
    },
    overline: {
      fontFamily: fontSans,
      fontWeight: 600,
      fontSize: '0.6875rem',
      letterSpacing: '0.08em',
    },
  },
  components: {
    // ─── Global Baseline ──────────────────────────────────────────────
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: palette.background.default,
          backgroundImage: 'none',
          minHeight: '100vh',
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
      },
    },
    // ─── Buttons ──────────────────────────────────────────────────────
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          textTransform: 'none',
          fontFamily: fontSans,
          fontWeight: 600,
          boxShadow: 'none',
          transition: 'all 0.15s ease',
          '&:hover': {
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          },
        },
        contained: {
          '&:hover': {
            boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
            transform: 'translateY(-1px)',
          },
        },
        containedPrimary: {
          background: palette.primary.main,
          '&:hover': {
            background: palette.primary.dark,
          },
        },
        outlined: {
          borderColor: '#E5E7EB',
          color: palette.text.primary,
          '&:hover': {
            borderColor: '#D1D5DB',
            backgroundColor: '#F9FAFB',
          },
        },
        outlinedPrimary: {
          borderColor: palette.primary.main,
          color: palette.primary.main,
          '&:hover': {
            borderColor: palette.primary.dark,
            backgroundColor: 'rgba(13, 148, 136, 0.06)',
          },
        },
      },
    },
    // ─── Paper ────────────────────────────────────────────────────────
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: palette.background.paper,
          border: '1px solid #E5E7EB',
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        },
        elevation0: {
          boxShadow: 'none',
          border: '1px solid #E5E7EB',
        },
      },
    },
    // ─── Cards ────────────────────────────────────────────────────────
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: palette.background.paper,
          border: '1px solid #E5E7EB',
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          transition: 'all 0.2s ease',
          '&:hover': {
            borderColor: '#D1D5DB',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    // ─── AppBar ───────────────────────────────────────────────────────
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
          borderBottom: '1px solid #E5E7EB',
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
          color: palette.text.primary,
        },
      },
    },
    // ─── Drawer ───────────────────────────────────────────────────────
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#FFFFFF',
          borderLeft: '1px solid #E5E7EB',
        },
      },
    },
    // ─── TextField ────────────────────────────────────────────────────
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            fontFamily: fontSans,
            backgroundColor: '#FFFFFF',
            '& fieldset': {
              borderColor: '#E5E7EB',
            },
            '&:hover fieldset': {
              borderColor: '#D1D5DB',
            },
            '&.Mui-focused fieldset': {
              borderColor: palette.primary.main,
              boxShadow: '0 0 0 3px rgba(13, 148, 136, 0.12)',
            },
          },
          '& .MuiInputLabel-root': {
            fontFamily: fontSans,
            color: palette.text.secondary,
          },
        },
      },
    },
    // ─── Dialog ───────────────────────────────────────────────────────
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: '#FFFFFF',
          border: '1px solid #E5E7EB',
          boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
        },
      },
    },
    // ─── Chip ─────────────────────────────────────────────────────────
    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: fontSans,
          fontWeight: 500,
        },
      },
    },
    // ─── Table ────────────────────────────────────────────────────────
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontFamily: fontSans,
          fontWeight: 600,
          color: palette.text.secondary,
          borderBottom: '1px solid #E5E7EB',
          backgroundColor: '#F9FAFB',
        },
        body: {
          fontFamily: fontSans,
          borderBottom: '1px solid #F3F4F6',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover td': {
            backgroundColor: '#F9FAFB',
          },
        },
      },
    },
    // ─── Tabs ─────────────────────────────────────────────────────────
    MuiTab: {
      styleOverrides: {
        root: {
          fontFamily: fontSans,
          fontWeight: 500,
          textTransform: 'none',
          minHeight: 44,
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: 2,
          borderRadius: '2px 2px 0 0',
        },
      },
    },
    // ─── List ─────────────────────────────────────────────────────────
    MuiListItemText: {
      styleOverrides: {
        primary: { fontFamily: fontSans },
        secondary: { fontFamily: fontSans },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontFamily: fontSans,
          '&:hover': {
            backgroundColor: '#F3F4F6',
          },
        },
      },
    },
    // ─── Accordion ────────────────────────────────────────────────────
    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
          border: '1px solid #E5E7EB',
          '&:before': { display: 'none' },
          boxShadow: 'none',
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        content: {
          '& .MuiTypography-root': {
            fontFamily: fontSans,
          },
        },
      },
    },
    // ─── Tooltip ──────────────────────────────────────────────────────
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#1F2937',
          color: '#F9FAFB',
          fontSize: '0.75rem',
          borderRadius: '6px',
          padding: '6px 12px',
        },
      },
    },
    // ─── Alert ────────────────────────────────────────────────────────
    MuiAlert: {
      styleOverrides: {
        standardSuccess: {
          backgroundColor: '#D1FAE5',
          color: '#065F46',
        },
        standardWarning: {
          backgroundColor: '#FEF3C7',
          color: '#92400E',
        },
        standardError: {
          backgroundColor: '#FEE2E2',
          color: '#991B1B',
        },
        standardInfo: {
          backgroundColor: '#DBEAFE',
          color: '#1E40AF',
        },
      },
    },
    // ─── Skeleton ─────────────────────────────────────────────────────
    MuiSkeleton: {
      styleOverrides: {
        root: {
          backgroundColor: '#F3F4F6',
        },
      },
    },
  },
});

// Export font families for direct use in sx props
export const fonts = {
  display: fontDisplay,
  sans: fontSans,
  mono: fontMono,
};
